// SocialButtons — placed on any user profile. Handles friend request, DM open, and video call.
// Video calls reuse the DM thread's dedicated LiveKit room name so both sides join the same room.
import { useEffect, useState } from "react";
import { UserPlus, UserCheck, UserX, MessageCircle, Video, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function SocialButtons({ otherUserId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ state: "loading" });

  const load = () => otherUserId && api.get(`/friends/status/${otherUserId}`).then(({ data }) => setState(data)).catch(() => setState({ state: "none" }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [otherUserId, user?.id]);

  if (!user || state.state === "self" || state.state === "anonymous") return null;

  const send = async () => {
    try { await api.post("/friends/request", { target_user_id: otherUserId }); toast.success("Friend request sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const accept = async () => { try { await api.post("/friends/accept", { request_id: state.request_id }); toast.success("You're now friends"); load(); } catch { toast.error("Failed"); } };
  const decline = async () => { try { await api.post("/friends/decline", { request_id: state.request_id }); toast.info("Declined"); load(); } catch { toast.error("Failed"); } };
  const openDm = async () => {
    try {
      const { data } = await api.post("/messenger/threads", { member_ids: [otherUserId] });
      nav(`/dm/${data.thread.id}`);
    } catch { toast.error("Could not open chat"); }
  };
  const startCall = async () => {
    try {
      const { data } = await api.post("/messenger/threads", { member_ids: [otherUserId], initial_message: "📞 started a video call" });
      nav(`/dm/${data.thread.id}?call=1`);
    } catch { toast.error("Could not start call"); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="social-buttons">
      {state.state === "friends" && <button className="btn-ghost rounded-full text-[10px]" onClick={() => toast.info("You are friends")}><UserCheck className="w-3.5 h-3.5" /> Friends</button>}
      {state.state === "outgoing" && <button className="btn-ghost rounded-full text-[10px] opacity-60" disabled><Loader2 className="w-3.5 h-3.5 animate-spin" /> Request sent</button>}
      {state.state === "incoming" && (
        <>
          <button className="btn-nexus text-[10px]" onClick={accept} data-testid="accept-friend"><UserCheck className="w-3.5 h-3.5" /> Accept</button>
          <button className="btn-ghost rounded-full text-[10px]" onClick={decline} data-testid="decline-friend"><UserX className="w-3.5 h-3.5" /> Decline</button>
        </>
      )}
      {state.state === "none" && <button className="btn-ghost rounded-full text-[10px]" onClick={send} data-testid="add-friend"><UserPlus className="w-3.5 h-3.5" /> Add Friend</button>}
      <button className="btn-ghost rounded-full text-[10px]" onClick={openDm} data-testid="dm-open"><MessageCircle className="w-3.5 h-3.5" /> Message</button>
      <button className="btn-ghost rounded-full text-[10px]" onClick={startCall} data-testid="dm-videocall"><Video className="w-3.5 h-3.5" /> Video Call</button>
    </div>
  );
}
